import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { Variables } from "graphql-request";

import { graphql } from "lib/generated/app-service-gql";
import {
  AiQuickPromptsQuery,
  CreateAiQuickPromptMutation,
  CreateAiQuickPromptMutationVariables,
  DeleteAiQuickPromptMutation,
  DeleteAiQuickPromptMutationVariables,
  ReorderAiQuickPromptsMutation,
  ReorderAiQuickPromptsMutationVariables,
  ResetAiQuickPromptsMutation,
  ResetAiQuickPromptsMutationVariables,
  UpdateAiQuickPromptMutation,
  UpdateAiQuickPromptMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { AiQuickPromptChatType } from "lib/types/graphQLEnums";
import { useGraphQlMutation } from "../useGraphQlClient";
import { AiQuickPrompt, aiQuickPromptsQueryKey } from "./useAiQuickPrompts";

// Shared by every quick prompts mutation, so an editor can tell when any change is saving
export const AI_QUICK_PROMPTS_MUTATION_KEY = ["aiQuickPromptsMutation"];

const createAiQuickPromptMutation = graphql(`
  mutation createAiQuickPrompt($input: CreateAiQuickPromptRequest!) {
    createAiQuickPrompt(input: $input) {
      prompts {
        id
        scope
        name
        prompt
      }
    }
  }
`);

const updateAiQuickPromptMutation = graphql(`
  mutation updateAiQuickPrompt($input: UpdateAiQuickPromptRequest!) {
    updateAiQuickPrompt(input: $input) {
      prompts {
        id
        scope
        name
        prompt
      }
    }
  }
`);

const deleteAiQuickPromptMutation = graphql(`
  mutation deleteAiQuickPrompt($input: DeleteAiQuickPromptRequest!) {
    deleteAiQuickPrompt(input: $input) {
      prompts {
        id
        scope
        name
        prompt
      }
    }
  }
`);

const reorderAiQuickPromptsMutation = graphql(`
  mutation reorderAiQuickPrompts($input: ReorderAiQuickPromptsRequest!) {
    reorderAiQuickPrompts(input: $input) {
      prompts {
        id
        scope
        name
        prompt
      }
    }
  }
`);

const resetAiQuickPromptsMutation = graphql(`
  mutation resetAiQuickPrompts($input: AiQuickPromptsRequest!) {
    resetAiQuickPrompts(input: $input) {
      prompts {
        id
        scope
        name
        prompt
      }
    }
  }
`);

// The list before an optimistic change, put back if the change fails
type MutationContext = { previousList?: AiQuickPrompt[] };

type MutationOptions<T, V> = UseMutationOptions<T, unknown, V, MutationContext>;

type ListVariables = Variables & { input: { chatType: AiQuickPromptChatType } };

/**
 * Every quick prompts mutation returns the chat's whole list, which goes straight into the cache.
 * With `optimisticList`, the list shows the change while it saves and goes back if it fails.
 */
function useAiQuickPromptsMutation<T, V extends ListVariables>(
  document: TypedDocumentNode<T, V>,
  listFrom: (data: T) => AiQuickPrompt[],
  options?: MutationOptions<T, V>,
  optimisticList?: (list: AiQuickPrompt[], variables: V) => AiQuickPrompt[],
) {
  const queryClient = useQueryClient();

  const setList = (chatType: AiQuickPromptChatType, list: AiQuickPrompt[]) =>
    queryClient.setQueryData<AiQuickPromptsQuery>(aiQuickPromptsQueryKey(chatType), {
      aiQuickPrompts: { prompts: list },
    });

  return useGraphQlMutation<T, V, MutationContext>(document, {
    ...options,
    mutationKey: AI_QUICK_PROMPTS_MUTATION_KEY,
    onMutate: async (vars) => {
      if (!optimisticList) return {};

      const queryKey = aiQuickPromptsQueryKey(vars.input.chatType);
      await queryClient.cancelQueries({ queryKey });
      const previousList =
        queryClient.getQueryData<AiQuickPromptsQuery>(queryKey)?.aiQuickPrompts.prompts;
      if (previousList) {
        setList(vars.input.chatType, optimisticList(previousList, vars));
      }
      return { previousList };
    },
    onSuccess: (data, vars, ctx) => {
      setList(vars.input.chatType, listFrom(data));
      options?.onSuccess?.(data, vars, ctx);
    },
    onError: (err, vars, ctx) => {
      if (ctx?.previousList) {
        setList(vars.input.chatType, ctx.previousList);
      }
      // Someone else in the team may have changed the list
      void queryClient.invalidateQueries({ queryKey: aiQuickPromptsQueryKey(vars.input.chatType) });
      options?.onError?.(err, vars, ctx);
    },
  });
}

// Adds a prompt to the end of the team's list for a chat
export function useCreateAiQuickPrompt(
  options?: MutationOptions<CreateAiQuickPromptMutation, CreateAiQuickPromptMutationVariables>,
) {
  return useAiQuickPromptsMutation(
    createAiQuickPromptMutation,
    (data) => data.createAiQuickPrompt.prompts,
    options,
  );
}

export function useUpdateAiQuickPrompt(
  options?: MutationOptions<UpdateAiQuickPromptMutation, UpdateAiQuickPromptMutationVariables>,
) {
  return useAiQuickPromptsMutation(
    updateAiQuickPromptMutation,
    (data) => data.updateAiQuickPrompt.prompts,
    options,
  );
}

export function useDeleteAiQuickPrompt(
  options?: MutationOptions<DeleteAiQuickPromptMutation, DeleteAiQuickPromptMutationVariables>,
) {
  return useAiQuickPromptsMutation(
    deleteAiQuickPromptMutation,
    (data) => data.deleteAiQuickPrompt.prompts,
    options,
    (list, { input }) => list.filter(({ id }) => id !== input.id),
  );
}

// Takes every prompt in the list, in the new order
export function useReorderAiQuickPrompts(
  options?: MutationOptions<ReorderAiQuickPromptsMutation, ReorderAiQuickPromptsMutationVariables>,
) {
  return useAiQuickPromptsMutation(
    reorderAiQuickPromptsMutation,
    (data) => data.reorderAiQuickPrompts.prompts,
    options,
    (list, { input }) =>
      input.ids.flatMap((id) => list.filter((quickPrompt) => quickPrompt.id === id)),
  );
}

// Drops the team's own list for a chat so it goes back to the chat's default quick prompts
export function useResetAiQuickPrompts(
  options?: MutationOptions<ResetAiQuickPromptsMutation, ResetAiQuickPromptsMutationVariables>,
) {
  return useAiQuickPromptsMutation(
    resetAiQuickPromptsMutation,
    (data) => data.resetAiQuickPrompts.prompts,
    options,
  );
}
